/**
 * O proximo passo de um plano (spec 007, UC-01, UC-07 e UC-21; spec 038, 1.5 e secao 3): o que o cliente ve e o botao
 * que resolve. O mesmo texto no cartao de Meu plano, nas Etapas e no destaque do Inicio. So traduz o que o backend
 * mandou (proximo passo, bloqueio, pedido aberto, avanco esperando a conta); nenhuma regra nova aqui.
 */
import { Aviso, Botao, BotaoLink, CampoCaixa, CampoData, CampoTextoLongo, Dialogo, IconeCalendario, formatarData, formatarDataHora, formatarDia, formatarReais, hojeEmSaoPaulo, type PassoDoPlano } from "@mtc/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { useAcao, useApi } from "./acoes";
import { nomeDaCobranca } from "./cobranca";
import { marca } from "./config";
import { contaDaGlobal } from "./corretora";
import { useComprovantes } from "./paginas/comprovantes";
import { ABERTAS, ETAPA_LONGA, type Matricula, type ModuloAprovacao, type Solicitacao } from "./tipos";

export type PassoPronto = { passo?: PassoDoPlano; acoes: ReactNode; dialogos: ReactNode };

/** O avanco aprovado esperando a conta nova, em texto (cartao, Inicio e o cartao da casca). */
export function textoDoAvancoAprovado(a: NonNullable<NonNullable<Matricula["modulos"]["etapas"]>["avanco_aguardando_liberacao"]>): string {
  const conta = a.para_rotulo ? `A conta de ${a.para_rotulo}` : "A conta nova";
  const prazo = a.prazo ? ` fica pronta em até ${a.prazo}` : " fica pronta no prazo combinado";
  const pago = a.pago_em ? ` Pagamento recebido em ${formatarData(a.pago_em)}.` : "";
  return `${conta}${prazo}.${pago} Você recebe o aviso aqui e por e-mail.`;
}

/**
 * Suspenso pela cobranca do mes (spec 064, taxa de manutencao da Funded): o resumo `mensalidade` traz o ultimo dia pra
 * pagar (`suspensa_ate`). Nulo: a suspensao e outra (a do administrador), e o cartao segue com "fale com o suporte".
 */
export function suspensaoPelaCobranca(m: Matricula): { ate: string; encerrado: boolean; nome: string } | null {
  const r = m.modulos.mensalidade;
  if (m.status !== "SUSPENSA" || !r?.suspensa_ate) return null;
  return { ate: r.suspensa_ate, encerrado: Boolean(r.proxima?.prazo_encerrado), nome: nomeDaCobranca(r.rotulo).toLowerCase() };
}

/** A frase do plano suspenso pela cobranca (cartao, Inicio e a pagina da cobranca). `semAbertura`: o titulo ja diz "suspenso". */
export function textoDaSuspensao(s: { ate: string; encerrado: boolean }, semAbertura = false): string {
  return s.encerrado
    ? `O prazo pra pagar acabou em ${formatarDia(s.ate)}. Pelo regulamento (5.3), a conta Funded é cancelada em definitivo; se pagar agora, o plano não volta sozinho e um administrador decide.`
    : `${semAbertura ? "" : "Plano suspenso por atraso. "}Pague até ${formatarDia(s.ate)} pra voltar a operar; depois disso a conta Funded é cancelada em definitivo (regulamento 5.3).`;
}

export function usePassoDoPlano(m: Matricula | null, solicitacoes: Solicitacao[], aoConcluir: (aviso: string) => void): PassoPronto {
  const [dialogo, setDialogo] = useState<"agendar" | "analise" | "aprovacao" | null>(null);
  const avanco = m ? solicitacoes.find((s) => s.matricula.id === m.id && s.tipo === "AVANCO_ETAPA" && ABERTAS.includes(s.status)) : undefined;
  // A aprovacao do Challenge (spec 064) vem no resumo `aprovacao`, com o ultimo pedido do plano.
  const pedidoDaAprovacao = m?.modulos.aprovacao?.pedido ?? null;
  const taxaAPagar = pedidoDaAprovacao?.status === "AGUARDANDO_PAGAMENTO" && !pedidoDaAprovacao.pago;
  // O avanco esperando pagamento com o comprovante ja enviado (spec 047): nao pede pagar de novo, mostra a espera.
  const comprovantes = useComprovantes((avanco?.status === "AGUARDANDO_PAGAMENTO" && !avanco.pagamento) || taxaAPagar);
  if (!m) return { acoes: null, dialogos: null };
  const e = m.modulos.etapas;
  const ap = m.modulos.aprovacao;
  const emAnalise = (id: string | undefined) => (id ? comprovantes.data?.itens.find((c) => c.alvo.tipo === "PEDIDO" && c.alvo.id === id && c.estado === "EM_ANALISE") : undefined);
  const comprovanteEmAnalise = emAnalise(avanco?.id);
  const suspensao = suspensaoPelaCobranca(m);
  const esperandoConta = e?.avanco_aguardando_liberacao ?? null;
  const pedidoAprovado = esperandoConta ? solicitacoes.find((s) => s.matricula.id === m.id && s.tipo === "AVANCO_ETAPA" && s.status === "APROVADA" && s.aguardando_liberacao) : undefined;
  let passo: PassoDoPlano | undefined;
  let acoes: ReactNode = null;
  const p = m.proximo_passo;
  if (m.status === "ENCERRADA") {
    // Duas saidas (Vitor, 30/09): comprar um plano novo, ou falar com o suporte se achar que o encerramento nao cabia.
    passo = { titulo: "Plano encerrado", texto: `${m.encerrada?.motivo ? `Motivo: ${m.encerrada.motivo}. ` : ""}Você pode começar de novo com um plano novo ou, se tiver alguma dúvida sobre o encerramento, falar com o suporte.`, tom: "erro" };
    const { whatsapp } = marca();
    const conversa = whatsapp ? `${whatsapp}${whatsapp.includes("?") ? "&" : "?"}text=${encodeURIComponent(`Olá! Meu plano ${m.plano.nome} foi encerrado e eu gostaria de entender o motivo.`)}` : null;
    acoes = (
      <>
        {/* Os planos a venda dentro da area (15/10), com este mesmo plano em primeiro pra comprar de novo. */}
        <BotaoLink href="/comprar" variante="primario">Comprar um novo plano</BotaoLink>
        {conversa && <a href={conversa} target="_blank" rel="noreferrer" className="mtc-botao mtc-botao--secundario">Falar com o suporte</a>}
      </>
    );
  }
  else if (m.bloqueio) {
    // Mensalidade vencida (spec 007, M9; spec 047): a area fica so pra pagar, e segue assim com o comprovante em analise
    // ate um administrador conferir. Mensagem, acao e destino vem do backend.
    passo = { titulo: "Área bloqueada", texto: m.bloqueio.mensagem, tom: "erro" };
    if (m.bloqueio.acao) acoes = <BotaoLink href={m.bloqueio.acao.link} variante="primario">{m.bloqueio.acao.rotulo}</BotaoLink>;
  } else if (suspensao) {
    // Suspenso pela taxa (spec 064, regulamento 5.3): o prazo e o caminho pra pagar, na pagina da cobranca.
    passo = { titulo: "Plano suspenso por atraso", texto: textoDaSuspensao(suspensao, true), tom: "erro" };
    acoes = <BotaoLink href="/mensalidades" variante={suspensao.encerrado ? "secundario" : "primario"}>{suspensao.encerrado ? `Ver a ${suspensao.nome}` : `Pagar a ${suspensao.nome}`}</BotaoLink>;
  } else if (m.status === "SUSPENSA") passo = { titulo: "Plano suspenso", texto: m.suspensa?.motivo ? `Motivo: ${m.suspensa.motivo}. Fale com o suporte.` : "Fale com o suporte para retomar.", tom: "alerta" };
  else if (p) {
    passo = { titulo: p.titulo, texto: p.texto, tom: p.tipo === "REAGENDAR" ? "alerta" : "info" };
    if (p.tipo === "AGUARDANDO_LIBERACAO") acoes = <Botao variante="secundario" icone={<IconeCalendario tamanho={16} />} onClick={() => setDialogo("agendar")}>Trocar a data</Botao>;
    else if (p.tipo === "AGENDAR_INICIO" || p.tipo === "REAGENDAR") acoes = <Botao icone={<IconeCalendario tamanho={16} />} onClick={() => setDialogo("agendar")}>Agendar início</Botao>;
  } else if (ap) {
    const r = passoDaAprovacao(m, ap, emAnalise(ap.pedido?.id)?.enviado_em ?? null, () => setDialogo("aprovacao"));
    passo = r.passo;
    acoes = r.acoes;
  } else if (avanco?.status === "AGUARDANDO_PAGAMENTO" && avanco.pagamento) {
    passo = { titulo: "Pagamento recebido", texto: "Um administrador está concluindo o seu avanço. Você recebe o aviso aqui e por e-mail.", tom: "info" };
    acoes = <BotaoLink href={`/solicitacoes/${avanco.id}`}>Acompanhar pedido</BotaoLink>;
  } else if (avanco?.status === "AGUARDANDO_PAGAMENTO" && comprovanteEmAnalise) {
    passo = { titulo: "Comprovante em análise", texto: `Recebemos seu comprovante em ${formatarData(comprovanteEmAnalise.enviado_em)}. A liberação leva até 1 dia depois do pagamento: um administrador confere e conclui o seu avanço.`, tom: "info" };
    acoes = <BotaoLink href={`/solicitacoes/${avanco.id}`}>Acompanhar pedido</BotaoLink>;
  } else if (avanco?.status === "AGUARDANDO_PAGAMENTO") {
    passo = { titulo: "Pague a mensalidade antecipada", texto: `Seu avanço foi aprovado. Pague ${avanco.valor_centavos != null ? formatarReais(avanco.valor_centavos) : "o valor informado"} pra liberar a nova etapa: por PIX ou cartão, com confirmação na hora, ou pelo PIX direto, com o comprovante.`, tom: "alerta" };
    acoes = <BotaoLink href={`/solicitacoes/${avanco.id}`} variante="primario">Pagar agora</BotaoLink>;
  } else if (avanco) {
    passo = { titulo: "Análise pedida", texto: `Você pediu a análise em ${formatarDataHora(avanco.criada_em)}. A equipe confere o resultado e decide.`, tom: "info" };
    acoes = <BotaoLink href={`/solicitacoes/${avanco.id}`}>Acompanhar pedido</BotaoLink>;
  } else if (esperandoConta) {
    // Aprovado e pago, esperando a equipe criar a conta (spec 038, 1.5): a promessa com o prazo, sem "Pedir analise".
    passo = { titulo: "Avanço aprovado", texto: textoDoAvancoAprovado(esperandoConta), tom: "info" };
    if (pedidoAprovado) acoes = <BotaoLink href={`/solicitacoes/${pedidoAprovado.id}`}>Acompanhar pedido</BotaoLink>;
  } else if (e?.teste_vencido) {
    passo = { titulo: "Prazo do teste encerrado", texto: "Seu plano aguarda a decisão da mesa.", tom: "alerta" };
  } else if (m.status === "ATIVA" && e?.proxima_etapa) {
    passo = { titulo: `Próxima etapa: ${ETAPA_LONGA[e.proxima_etapa]}`, texto: "Quando bater a meta, peça a análise do resultado. A equipe confere na plataforma e decide.", tom: "info" };
    acoes = <Botao onClick={() => setDialogo("analise")}>Pedir análise do resultado</Botao>;
  } else if (m.status === "ATIVA" && e && !e.proxima_etapa) {
    passo = { titulo: `Você está na ${e.etapa_rotulo}`, texto: "É a última etapa do seu plano.", tom: "sucesso" };
  }
  const dialogos = (
    <>
      <DialogoAgendar m={m} aberto={dialogo === "agendar"} aoFechar={() => setDialogo(null)} aoConcluir={aoConcluir} />
      <DialogoAnalise m={m} aberto={dialogo === "analise"} aoFechar={() => setDialogo(null)} aoConcluir={aoConcluir} />
      {ap && <DialogoAprovacao m={m} prazo={ap.prazo_da_analise} aberto={dialogo === "aprovacao"} aoFechar={() => setDialogo(null)} aoConcluir={aoConcluir} />}
    </>
  );
  return { passo, acoes, dialogos };
}

function DialogoAgendar({ m, aberto, aoFechar, aoConcluir }: { m: Matricula; aberto: boolean; aoFechar: () => void; aoConcluir: (aviso: string) => void }) {
  const { postar } = useApi();
  const qc = useQueryClient();
  const acao = useAcao();
  const hoje = hojeEmSaoPaulo();
  const atual = m.modulos.agendamento?.inicio;
  const [data, setData] = useState(atual ?? hoje);
  useEffect(() => { if (aberto) { setData(atual ?? hoje); acao.limpar(); } }, [aberto]);
  const enviar = async (ev: FormEvent) => {
    ev.preventDefault();
    const ok = await acao.rodar(() => postar(`/v1/me/matriculas/${m.id}/agendamento`, { data }));
    if (!ok) return;
    await qc.invalidateQueries();
    aoFechar();
    aoConcluir(`${atual ? "Nova data" : "Início agendado"}: ${formatarDia(data)}. A equipe libera sua conta nessa data.`);
  };
  return (
    <Dialogo aberto={aberto} aoFechar={aoFechar} titulo={atual ? "Trocar a data de início" : "Agendar início"} descricao={`${m.plano.nome}. Escolha o dia em que você quer começar; a equipe libera sua conta nessa data.`}>
      <form className="mtc-formulario" onSubmit={enviar} noValidate>
        {acao.erro && <Aviso tipo="erro">{acao.erro}</Aviso>}
        {atual && <Aviso tipo="info">Data atual: <strong>{formatarDia(atual)}</strong>.</Aviso>}
        <CampoData rotulo="Data de início" valor={data} aoMudar={setData} min={hoje} required dica="Só dias úteis, de hoje em diante. Dá pra trocar até a liberação." />
        <div className="mtc-dialogo__rodape">
          <Botao variante="fantasma" onClick={aoFechar}>Cancelar</Botao>
          <Botao type="submit" carregando={acao.carregando} disabled={!data}>Confirmar data</Botao>
        </div>
      </form>
    </Dialogo>
  );
}

function DialogoAnalise({ m, aberto, aoFechar, aoConcluir }: { m: Matricula; aberto: boolean; aoFechar: () => void; aoConcluir: (aviso: string) => void }) {
  const { postar } = useApi();
  const qc = useQueryClient();
  const acao = useAcao();
  const [mensagem, setMensagem] = useState("");
  const e = m.modulos.etapas;
  useEffect(() => { if (aberto) { setMensagem(""); acao.limpar(); } }, [aberto]);
  const enviar = async (ev: FormEvent) => {
    ev.preventDefault();
    const ok = await acao.rodar(() => postar(`/v1/me/matriculas/${m.id}/avanco`, { mensagem: mensagem.trim() || undefined }));
    if (!ok) return;
    await qc.invalidateQueries();
    aoFechar();
    aoConcluir("Pedido de análise enviado. A equipe confere o resultado e avisa você aqui e por e-mail.");
  };
  return (
    <Dialogo aberto={aberto} aoFechar={aoFechar} titulo="Pedir análise do resultado" descricao={e?.proxima_etapa ? `${m.plano.nome}: você está em ${e.etapa_rotulo}. Se a equipe aprovar, o plano vai para ${ETAPA_LONGA[e.proxima_etapa]}.` : m.plano.nome}>
      <form className="mtc-formulario" onSubmit={enviar} noValidate>
        {acao.erro && <Aviso tipo="erro">{acao.erro}</Aviso>}
        <CampoTextoLongo rotulo="Mensagem para a equipe (opcional)" valor={mensagem} aoMudar={setMensagem} maxLength={500} placeholder="Ex.: bati a meta em 12 pregões, sem estourar o limite diário." dica="A equipe confere o resultado na plataforma antes de decidir." />
        <div className="mtc-dialogo__rodape">
          <Botao variante="fantasma" onClick={aoFechar}>Cancelar</Botao>
          <Botao type="submit" carregando={acao.carregando}>Enviar pedido</Botao>
        </div>
      </form>
    </Dialogo>
  );
}

/**
 * O passo do plano da Global no Challenge (spec 064, secao 4): pedir a aprovacao, a analise com o dever de pausar, a
 * recusa com o motivo, a taxa de ativacao a pagar, a conta Funded a caminho e a Funded. Tudo vem do resumo `aprovacao`
 * e da etapa do `corretora`; se pode pedir e por que nao, quem diz e o backend.
 */
function passoDaAprovacao(m: Matricula, ap: ModuloAprovacao, comprovanteEnviadoEm: string | null, pedir: () => void): { passo?: PassoDoPlano; acoes: ReactNode } {
  const conta = contaDaGlobal(m);
  const p = ap.pedido;
  const acompanhar = p ? <BotaoLink href={`/solicitacoes/${p.id}`}>Acompanhar pedido</BotaoLink> : null;
  if (conta?.etapa === "FUNDED") {
    return { passo: { titulo: "Você está na Funded", texto: `Sua conta Funded está liberada${conta.etapa_desde ? ` desde ${formatarDia(conta.etapa_desde)}` : ""}. Siga as regras do regulamento pra manter a conta.`, tom: "sucesso" }, acoes: null };
  }
  if (p?.status === "PENDENTE") {
    return {
      passo: { titulo: "Aprovação em análise", texto: `Você pediu a aprovação em ${formatarDataHora(p.criado_em)}. A análise leva até ${ap.prazo_da_analise}. Mantenha as operações pausadas até a resposta: se operar e atingir o drawdown, a conta é cancelada.`, tom: "alerta" },
      acoes: acompanhar,
    };
  }
  if (p?.status === "AGUARDANDO_PAGAMENTO") {
    if (p.pago) return { passo: { titulo: "Pagamento recebido", texto: "A taxa de ativação foi paga. Um administrador está liberando a sua conta Funded; você recebe o aviso aqui e por e-mail.", tom: "info" }, acoes: acompanhar };
    if (comprovanteEnviadoEm) return { passo: { titulo: "Comprovante em análise", texto: `Recebemos seu comprovante em ${formatarData(comprovanteEnviadoEm)}. Um administrador confere em até 1 dia e, com o pagamento confirmado, a conta Funded é liberada.`, tom: "info" }, acoes: acompanhar };
    return {
      passo: { titulo: "Pague a taxa de ativação", texto: `Seu Challenge foi aprovado. Pague ${p.valor_centavos != null ? formatarReais(p.valor_centavos) : "o valor informado"} de taxa de ativação pra liberar a conta Funded: por PIX ou cartão, com confirmação na hora, ou pelo PIX direto, com o comprovante.`, tom: "alerta" },
      acoes: <BotaoLink href={`/solicitacoes/${p.id}`} variante="primario">Pagar a taxa de ativação</BotaoLink>,
    };
  }
  if (p?.status === "APROVADA" && p.aguardando_liberacao) {
    return {
      passo: { titulo: "Challenge aprovado", texto: `A conta Funded fica pronta em até ${p.prazo ?? "o prazo combinado"}.${p.pago && p.valor_centavos ? " Taxa de ativação paga." : ""} Você recebe o aviso aqui e por e-mail.`, tom: "info" },
      acoes: acompanhar,
    };
  }
  const pedirBotao = ap.pode_pedir ? <Botao onClick={pedir}>{p?.status === "RECUSADA" ? "Pedir aprovação de novo" : "Pedir aprovação"}</Botao> : null;
  if (p?.status === "RECUSADA") {
    return {
      passo: { titulo: "Aprovação não concedida", texto: `${p.observacao ? `Motivo: ${p.observacao}. ` : ""}${ap.pode_pedir ? "Quando bater a meta de novo, peça outra análise." : (ap.motivo ?? "")}`.trim(), tom: "alerta" },
      acoes: pedirBotao,
    };
  }
  if (ap.pode_pedir) {
    return { passo: { titulo: "Challenge em andamento", texto: `Bateu a meta? Peça a aprovação do Challenge. A análise leva até ${ap.prazo_da_analise}, e as operações ficam pausadas até a resposta.`, tom: "info" }, acoes: pedirBotao };
  }
  return { passo: ap.motivo ? { titulo: m.status === "ATIVA" ? "Challenge em andamento" : "Aprovação do Challenge", texto: ap.motivo, tom: "info" } : undefined, acoes: null };
}

/**
 * Pedir a aprovacao do Challenge (spec 064; regulamento 4.5): o dever de pausar as operacoes ate a analise terminar,
 * confirmado na caixa (sem ela, o botao fica apagado e o backend recusa). A mensagem e opcional.
 */
function DialogoAprovacao({ m, prazo, aberto, aoFechar, aoConcluir }: { m: Matricula; prazo: string; aberto: boolean; aoFechar: () => void; aoConcluir: (aviso: string) => void }) {
  const { postar } = useApi();
  const qc = useQueryClient();
  const acao = useAcao();
  const [mensagem, setMensagem] = useState("");
  const [ciente, setCiente] = useState(false);
  useEffect(() => { if (aberto) { setMensagem(""); setCiente(false); acao.limpar(); } }, [aberto]);
  const enviar = async (ev: FormEvent) => {
    ev.preventDefault();
    if (!ciente) return;
    const ok = await acao.rodar(() => postar(`/v1/me/matriculas/${m.id}/aprovacao`, { ciente_de_pausar: true, mensagem: mensagem.trim() || undefined }));
    if (!ok) return;
    await qc.invalidateQueries();
    aoFechar();
    aoConcluir(`Pedido de aprovação enviado. Mantenha as operações pausadas: a resposta sai em até ${prazo}, aqui e por e-mail.`);
  };
  return (
    <Dialogo aberto={aberto} aoFechar={aoFechar} titulo="Pedir aprovação do Challenge" descricao={`${m.plano.nome}. A equipe confere as regras e a meta na corretora e responde em até ${prazo}.`}>
      <form className="mtc-formulario" onSubmit={enviar} noValidate>
        {acao.erro && <Aviso tipo="erro">{acao.erro}</Aviso>}
        <Aviso tipo="alerta"><strong>Pause as operações depois de pedir.</strong> Pelo regulamento, as operações ficam pausadas até a análise terminar, o que leva até {prazo}. Se você continuar operando e atingir o drawdown, a conta é cancelada.</Aviso>
        <CampoTextoLongo rotulo="Mensagem para a equipe (opcional)" valor={mensagem} aoMudar={setMensagem} maxLength={500} placeholder="Ex.: bati a meta em 12 pregões, sem estourar o limite diário." />
        <CampoCaixa marcado={ciente} aoMudar={setCiente}>Vou pausar as operações até a análise terminar</CampoCaixa>
        <div className="mtc-dialogo__rodape">
          <Botao variante="fantasma" onClick={aoFechar}>Cancelar</Botao>
          <Botao type="submit" carregando={acao.carregando} disabled={!ciente}>Enviar pedido</Botao>
        </div>
      </form>
    </Dialogo>
  );
}
